


import React, { useState, useEffect, useRef } from "react";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };

  const ChevronRight = () => (
    <svg className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  );

  return (
    <>
      {/* Google Fonts Import - Futuristic Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:wght@300;400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      
      <section 
        ref={sectionRef}
        id="about" 
        className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative min-h-screen"
        style={{fontFamily: 'Orbitron, monospace'}}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute top-20 left-20 w-40 h-40 bg-cyan-500 rounded-full blur-xl animate-pulse"
            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
          ></div>
          <div 
            className="absolute bottom-20 right-32 w-32 h-32 bg-purple-500 rounded-full blur-lg animate-bounce"
            style={{ transform: `translateY(${scrollY * -0.05}px)` }}
          ></div>
          <div 
            className="absolute top-1/2 left-1/3 w-24 h-24 bg-pink-500 rounded-full blur-md animate-ping"
            style={{ transform: `translateY(${scrollY * 0.08}px)` }}
          ></div>
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 bg-cyan-400 rounded-full animate-pulse opacity-60`}
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 3) * 20}%`,
                animationDelay: `${i * 0.5}s`,
                transform: `translateY(${Math.sin(scrollY * 0.01 + i) * 20}px)`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-4 ml-96 relative z-10">
          {/* Section Title with Flying Animation */}
          <div className="text-center mb-16">
            <div className={`transform transition-all duration-2000 ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-96 opacity-0'
            }`}>
              <h2 
                className="text-5xl md:text-7xl font-bold text-white mb-6 relative"
                style={{
                  transform: `perspective(1000px) rotateX(${mousePosition.y * 2}deg) rotateY(${mousePosition.x * 2}deg)`,
                  textShadow: '0 0 20px rgba(34, 211, 238, 0.5)'
                }}
                onMouseMove={handleMouseMove}
              >
                <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-pulse font-black tracking-wider">
                  ABOUT ME
                </span>
                
                {/* Glowing Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent blur-lg opacity-30 animate-pulse">
                  ABOUT ME
                </div>
              </h2>
            </div>
            
            <div className={`transform transition-all duration-2000 delay-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
            }`}>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light tracking-wide" style={{fontFamily: 'Exo 2, sans-serif'}}>
                Blockchain Developer with expertise in Hyperledger Fabric and
                Ethereum smart contracts. Seeking opportunities to develop
                decentralized solutions in collaborative environments.
              </p>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
              {/* Profile Image with 3D Effects */}
              <div className={`lg:col-span-1 transform transition-all duration-1500 delay-700 ${
                isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
              }`}>
                <div className="relative group">
                  {/* Rotating Border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-lg blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-lg animate-spin-slow opacity-50"></div>
                  
                  <div 
                    className="relative bg-white/10 backdrop-blur-sm rounded-lg p-3 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30"
                    onMouseMove={handleMouseMove}
                    style={{
                      transform: `perspective(1000px) rotateX(${mousePosition.y * 3}deg) rotateY(${mousePosition.x * 3}deg) scale(${isVisible ? 1 : 0.8})`
                    }}
                  >
                    <img
                      src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
                      alt="Maneesh profile"
                      className="w-full h-auto rounded-lg shadow-2xl transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110"
                    />
                    
                    {/* Overlay Effects */}
                    <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12 rounded-lg"></div>
                  </div>

                  {/* Floating Elements */}
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-cyan-500 rounded-full animate-bounce opacity-80"></div>
                  <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
                  <div className="absolute top-1/2 -left-6 w-4 h-4 bg-purple-500 rounded-full animate-ping opacity-60"></div>
                </div>
              </div>

              {/* Content */}
              <div className={`lg:col-span-2 transform transition-all duration-1500 delay-1000 ${
                isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
              }`}>
                <h2 
                  className="text-3xl md:text-4xl font-bold text-cyan-300 mb-6 hover:scale-105 transition-transform duration-300 cursor-default"
                  style={{ textShadow: '0 0 10px rgba(34, 211, 238, 0.3)' }}
                >
                  BLOCKCHAIN DEVELOPER
                </h2>
                
                <p className="text-lg text-gray-300 mb-8 leading-relaxed italic font-light tracking-wide" style={{fontFamily: 'Exo 2, sans-serif'}}>
                  PG Diploma in Blockchain Technology (2025) from Digital
                  University Kerala and B.Tech in Electrical & Electronics (2018)
                  from CUSAT. Passionate about Web3 governance, tokenomics,
                  enterprise integration, and blockchain identity systems.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {[
                      { label: "Phone:", value: "+91 8281422036", delay: 1200 },
                      { label: "Location:", value: "Kerala, India", delay: 1300 },
                      { label: "Email:", value: "maneeshroks@gmail.com", delay: 1400 },
                      { label: "Freelance:", value: "Available", delay: 1500 }
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-start space-x-3 transform transition-all duration-1000 hover:translate-x-2 hover:scale-105 group cursor-default ${
                          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                        style={{ transitionDelay: `${item.delay}ms` }}
                      >
                        <ChevronRight />
                        <div className="group-hover:text-cyan-300 transition-colors duration-300">
                          <span className="font-semibold text-white">
                            {item.label}
                          </span>
                          <span className="text-gray-300 ml-2">{item.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {[
                      { label: "PG Diploma:", value: "Blockchain Technology, Digital University Kerala (2025)", delay: 1600 },
                      { label: "B.Tech:", value: "Electrical & Electronics, CUSAT (2018)", delay: 1700 },
                      { label: "Languages:", value: "English, Malayalam, Hindi, Tamil", delay: 1800 },
                      { label: "Tools:", value: "Git, Docker, Remix, Postman, VS Code", delay: 1900 }
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-start space-x-3 transform transition-all duration-1000 hover:translate-x-2 hover:scale-105 group cursor-default ${
                          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                        }`}
                        style={{ transitionDelay: `${item.delay}ms` }}
                      >
                        <ChevronRight />
                        <div className="group-hover:text-cyan-300 transition-colors duration-300">
                          <span className="font-semibold text-white">
                            {item.label}
                          </span>
                          <span className="text-gray-300 ml-2 block">
                            {item.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Left Sidebar Space */}
        <div className="hidden lg:block fixed left-0 top-0 w-96 h-full bg-gradient-to-r from-slate-900/30 to-transparent pointer-events-none z-0"></div>

        {/* Custom CSS for additional animations */}
        <style jsx>{`
          @keyframes spin-slow {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          
          .animate-spin-slow {
            animation: spin-slow 8s linear infinite;
          }
          
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
        `}</style>
      </section>
    </>
  );
};

export default About;


// import React from "react";
// import { motion } from "framer-motion";
// import { FaChevronRight } from "react-icons/fa";

// const About = () => {
//   return (
//     <section id="about" className="py-20 bg-white">
//       <div className="container mx-auto px-4">
//         {/* Section Title */}
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           viewport={{ once: true }}
//           className="text-center mb-16"
//         >
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
//             ABOUT ME
//           </h2>
//           <p className="text-xl text-gray-600 max-w-3xl mx-auto">
//             Blockchain Developer with expertise in Hyperledger Fabric and
//             Ethereum smart contracts. Seeking opportunities to develop
//             decentralized solutions in collaborative environments.
//           </p>
//         </motion.div>

//         <div className="max-w-6xl mx-auto">
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
//             {/* Profile Image */}
//             <motion.div
//               initial={{ opacity: 0, x: -50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.8, delay: 0.2 }}
//               viewport={{ once: true }}
//               className="lg:col-span-1"
//             >
//               <div className="relative">
//                 <img
//                   src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&crop=face"
//                   alt="Maneesh profile"
//                   className="w-full h-auto rounded-lg shadow-2xl"
//                 />
//                 <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-lg"></div>
//               </div>
//             </motion.div>

//             {/* Content */}
//             <motion.div
//               initial={{ opacity: 0, x: 50 }}
//               whileInView={{ opacity: 1, x: 0 }}
//               transition={{ duration: 0.8, delay: 0.4 }}
//               viewport={{ once: true }}
//               className="lg:col-span-2"
//             >
//               <h2 className="text-3xl font-bold text-gray-900 mb-6">
//                 Blockchain Developer
//               </h2>
//               <p className="text-lg text-gray-600 mb-8 leading-relaxed italic">
//                 PG Diploma in Blockchain Technology (2025) from Digital
//                 University Kerala and B.Tech in Electrical & Electronics (2018)
//                 from CUSAT. Passionate about Web3 governance, tokenomics,
//                 enterprise integration, and blockchain identity systems.
//               </p>

//               <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
//                 {/* Left Column */}
//                 <div className="space-y-4">
//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 0.6 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Phone:
//                       </span>
//                       <span className="text-gray-600 ml-2">+91 8281422036</span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 0.7 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Location:
//                       </span>
//                       <span className="text-gray-600 ml-2">Kerala, India</span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 0.8 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Email:
//                       </span>
//                       <span className="text-gray-600 ml-2">
//                         maneeshroks@gmail.com
//                       </span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 0.9 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Freelance:
//                       </span>
//                       <span className="text-gray-600 ml-2">Available</span>
//                     </div>
//                   </motion.div>
//                 </div>

//                 {/* Right Column */}
//                 <div className="space-y-4">
//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 1.0 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         PG Diploma:
//                       </span>
//                       <span className="text-gray-600 ml-2 block">
//                         Blockchain Technology, Digital University Kerala (2025)
//                       </span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 1.1 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         B.Tech:
//                       </span>
//                       <span className="text-gray-600 ml-2 block">
//                         Electrical & Electronics, CUSAT (2018)
//                       </span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 1.2 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Languages:
//                       </span>
//                       <span className="text-gray-600 ml-2">
//                         English, Malayalam, Hindi, Tamil
//                       </span>
//                     </div>
//                   </motion.div>

//                   <motion.div
//                     initial={{ opacity: 0, y: 20 }}
//                     whileInView={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.6, delay: 1.3 }}
//                     viewport={{ once: true }}
//                     className="flex items-start space-x-3"
//                   >
//                     <FaChevronRight className="text-blue-500 mt-1 flex-shrink-0" />
//                     <div>
//                       <span className="font-semibold text-gray-900">
//                         Tools:
//                       </span>
//                       <span className="text-gray-600 ml-2">
//                         Git, Docker, Remix, Postman, VS Code
//                       </span>
//                     </div>
//                   </motion.div>
//                 </div>
//               </div>
//             </motion.div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default About;
